import { Box, Button, Collapse, FormControl, TextareaAutosize, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchRecipeData, scrapeRecipe } from "../../services/openAiRecipeService";
import LoadingSpinner from "../Common/LoadingSpinner";
import MessageAlert from "../Common/MessageAlert";

function isUrl(str) {
  try {
    new URL(str);
    return true;
  } catch (error) {
    return false;
  }
}

function isLikelyInvalid(input) {
  // Very short => skip
  if (input.trim().length < 10) return true;
  // Could add more checks if needed
  return false;
}

function RecipeDataImporter({ onJsonParsed }) {
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");

  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const handleParseRecipe = async () => {
    const trimmed = input.trim();
    if (!trimmed) {
      setError(t("recipeImporter.alert"));
      return;
    }

    if (isUrl(trimmed) === false && isLikelyInvalid(trimmed)) {
      // e.g. user typed random short text -> skip
      setError(t("recipeImporter.invalidData"));
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      let responseJson = null;

      if (isUrl(trimmed)) {
        responseJson = await scrapeRecipe(trimmed, lang);
      } else {
        responseJson = await fetchRecipeData(trimmed, lang);
      }

      if (!responseJson) {
        throw new Error(t("recipeImporter.invalidResponse"));
      }

      onJsonParsed(responseJson);
    } catch (err) {
      const msg = t("recipeImporter.error", {
        error: err.message || t("errors.generic"),
      });
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box mt={2}>
      <Button variant="outlined" color="primary" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? t("recipeImporter.hide") : t("recipeImporter.show")}
      </Button>

      {error && (
        <MessageAlert severity="error" onClose={() => setError("")}>
          {error}
        </MessageAlert>
      )}

      <Collapse in={isOpen}>
        <FormControl fullWidth>
          <TextareaAutosize
            minRows={6}
            placeholder={t("recipeImporter.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            style={{ width: "100%", padding: "10px", fontSize: "16px" }}
          />
          <Typography variant="caption" color="gray" sx={{ position: "absolute", bottom: 8, right: 10 }}>
            {t("recipeImporter.poweredByAI", { provider: "OpenAI" })}
          </Typography>
        </FormControl>
        <Box mt={2}>
          <Button variant="contained" color="primary" onClick={handleParseRecipe} disabled={isLoading}>
            {isLoading ? t("recipeImporter.loading") : t("recipeImporter.fillFields")}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

export default RecipeDataImporter;
